import { Link, useNavigate } from "react-router-dom";
import { assetUrl } from "../api/client";
import StatusBadge, { ModeBadge } from "./StatusBadge";
import PipelineProgressBar from "./PipelineProgressBar";
import ScoreBadge from "./ScoreBadge";
import PiiChip from "./PiiChip";
import CategoryBadge from "./CategoryBadge";
import DeductionFooter from "./DeductionFooter";
import FraudChip from "./FraudChip";
import { formatDuration, recordingDate, timeAgo } from "../lib/format";
import { gateFailed } from "../lib/gate";
import type { RecordingListItem } from "../api/types";

interface Props {
  rec: RecordingListItem;
  /** Batch-review mode: render a checkbox on the thumbnail. */
  selectable?: boolean;
  selected?: boolean;
  /** Absent (with selectable set) = this recording cannot be batch-reviewed. */
  onToggleSelect?: (id: number) => void;
}

/** One recording tile — shared by the Review, Processing and Recordings pages. */
export default function RecordingCard({ rec, selectable, selected, onToggleSelect }: Props) {
  const qc = rec.qc;
  const shortId = rec.log_id.length > 18 ? `${rec.log_id.slice(0, 17)}…` : rec.log_id;
  // Lead with the person who recorded it; legacy rows without a resolvable uploader fall
  // back to the log id (the id stays available in the tooltip either way).
  const title = rec.user_name ?? shortId;
  // in batch mode the tile toggles selection instead of navigating; the corner button
  // keeps the detail page one click away.
  const picking = Boolean(selectable && onToggleSelect);
  const navigate = useNavigate();
  const cls = ["rec-card", picking ? "pickable" : "", selected ? "picked" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <Link
      to={`/r/${rec.id}`}
      className={cls}
      onClick={(e) => {
        if (!picking) return;
        e.preventDefault();
        onToggleSelect?.(rec.id);
      }}
    >
      <div className="rec-thumb">
        {rec.thumbnail_url ? (
          <img src={assetUrl(rec.thumbnail_url)} alt="" loading="lazy" />
        ) : (
          <div className="rec-thumb-empty" />
        )}
        {picking && (
          <>
            <span className="rec-pick" aria-hidden>
              <input type="checkbox" checked={Boolean(selected)} readOnly tabIndex={-1} />
            </span>
            <button
              type="button"
              className="rec-open"
              title="Open recording"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/r/${rec.id}`);
              }}
            >
              Open
            </button>
          </>
        )}
        <div className="rec-thumb-badges">
          <ModeBadge mode={rec.mode} />
          <StatusBadge status={rec.status} />
          {gateFailed(qc) && (
            <span className="gate-fail-badge" title="Failed the QC gate — no value as delivered">
              QC FAIL
            </span>
          )}
          {qc?.is_emulator && (
            <span className="emulator-badge" title="Synthetic emulator capture">
              EMULATOR
            </span>
          )}
          <PiiChip pii={qc?.pii} compact className="on-thumb" />
        </div>
        {(qc?.score != null || qc?.grade != null) && (
          <div className="rec-score">
            <ScoreBadge score={qc?.score ?? null} grade={qc?.grade ?? null} size={48} />
          </div>
        )}
      </div>

      <div className="rec-card-body">
        <div className="rec-id-row">
          <span className={rec.user_name ? "rec-id" : "rec-id mono"} title={rec.log_id}>
            {title}
          </span>
          <span className="rec-org">{rec.org}</span>
        </div>

        {rec.task && (rec.task.title || rec.task.category) && (
          <div className="rec-usertask">
            <CategoryBadge task={rec.task} />
            {rec.task.title && (
              <span className="rec-usertask-title" title={rec.task.title}>
                {rec.task.title}
              </span>
            )}
          </div>
        )}

        {rec.fraud && rec.fraud.verdict !== "clear" && (
          <div className="rec-fraud">
            <FraudChip fraud={rec.fraud} status={rec.status} />
            {rec.fraud.signals.slice(0, 2).map((sg) => (
              <span key={sg.id} className={`fraud-signal ${sg.level}`} title={sg.detail}>
                {sg.id.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        )}

        {(qc?.llm_is_real != null || qc?.llm_task) && (
          <div className="rec-ai">
            {qc?.llm_is_real != null && (
              <span
                className={`ai-chip ${qc.llm_is_real ? "real" : "synthetic"}`}
                title={qc.llm_is_real ? "AI Check: real-world capture" : "AI Check: likely synthetic"}
              >
                {qc.llm_is_real ? "✓ Real" : "⚠ Synthetic"}
              </span>
            )}
            {qc?.llm_task && (
              <span className="rec-task" title={qc.llm_task}>
                {qc.llm_task}
              </span>
            )}
          </div>
        )}

        {rec.progress ? (
          // in-flight: show where the pipeline is instead of the recording facts
          <PipelineProgressBar progress={rec.progress} />
        ) : (
          // recording facts (the qc/technical stats live on the detail page)
          <div className="rec-info">
            <div className="rec-info-duration">
              <span className="rec-duration-value">{formatDuration(rec.duration_ms)}</span>
              <span className="rec-duration-label">duration</span>
            </div>
            <div className="rec-info-facts">
              <span className="rec-recorded" title="When the recording was captured">
                {recordingDate(rec.log_id, rec.created_at)}
              </span>
              <span className="rec-device">{rec.device_model ?? "unknown device"}</span>
            </div>
          </div>
        )}

        {!rec.progress && <DeductionFooter qc={qc} />}

        <div className="rec-foot muted small">
          <span title={new Date(rec.created_at).toISOString()}>
            added {timeAgo(rec.created_at)}
          </span>
        </div>
      </div>
    </Link>
  );
}
